import React from 'react';
import {
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  LinkIcon,
  BookOpenIcon,
} from '@heroicons/react/outline';
import {
  Feature,
  PatientContact,
  useProgramNoteSharesForContactQuery,
} from '../../../../../generated/graphql';
import {
  CONTACT_TYPE_LABELS,
  getContactFullName,
  formatAddress,
} from './constants';
import Button from '../../../../components/Button';
import { useFeatureFlags } from '../../../../../contexts/FeatureFlagContext';

interface ContactCardProps {
  contact: PatientContact;
  onEdit: (contact: PatientContact) => void;
  onDelete: (contact: PatientContact) => void;
  onSendForm?: (contact: PatientContact) => void;
  onViewLinks?: (contact: PatientContact) => void;
  onViewSentNotes?: (contact: PatientContact) => void;
}

const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  onEdit,
  onDelete,
  onSendForm,
  onViewLinks,
  onViewSentNotes,
}) => {
  const fullName = getContactFullName(contact);
  const address = formatAddress(contact);
  const { isFeatureEnabled } = useFeatureFlags();
  const isExternalNoteShareEnabled = isFeatureEnabled(
    Feature.ExternalNoteShare,
  );

  // When the flag is off, only show "View sent notes" if this contact already
  // has shares (any status) so in-flight links remain manageable (Option B).
  // network-only avoids a stale empty cache after shares were created while the
  // flag was on (query was skipped). Fail open on error so management isn't hidden.
  const {
    data: noteSharesData,
    loading: noteSharesLoading,
    error: noteSharesError,
  } = useProgramNoteSharesForContactQuery({
    variables: { contactId: contact.id ?? '' },
    skip: !contact.id || !onViewSentNotes || isExternalNoteShareEnabled,
    fetchPolicy: 'network-only',
  });
  const hasNoteShares =
    (noteSharesData?.programNoteSharesForContact?.length ?? 0) > 0;
  const showViewSentNotes = Boolean(
    onViewSentNotes &&
      (isExternalNoteShareEnabled ||
        Boolean(noteSharesError) ||
        (!noteSharesLoading && hasNoteShares)),
  );

  return (
    <div className="w-full rounded border border-neutral-50 bg-neutral-50 p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-body text-neutral-150">
              {fullName || 'Unnamed Contact'}
            </span>
            {contact.contactType && (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-small-caption font-medium text-neutral-150">
                {CONTACT_TYPE_LABELS[contact.contactType] ||
                  contact.contactType}
              </span>
            )}
          </div>
          {contact.relationshipDescription && (
            <p className="text-caption text-neutral-125">
              {contact.relationshipDescription}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onEdit(contact)}
            className="rounded p-1.5 text-neutral-125 transition-colors hover:bg-neutral-50 hover:text-neutral-150"
            aria-label="Edit contact"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(contact)}
            className="rounded p-1.5 text-red-100 transition-colors hover:bg-red-25"
            aria-label="Delete contact"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-1 text-caption text-neutral-125">
        {contact.email && (
          <div className="flex items-start">
            <span className="mr-2 font-medium text-neutral-110">Email:</span>
            <a
              href={`mailto:${contact.email}`}
              className="text-green-150 hover:underline"
            >
              {contact.email}
            </a>
          </div>
        )}
        {contact.phoneNumber && (
          <div className="flex items-start">
            <span className="mr-2 font-medium text-neutral-110">Phone:</span>
            <a
              href={`tel:${contact.phoneNumber}`}
              className="text-green-150 hover:underline"
            >
              {contact.phoneNumber}
            </a>
          </div>
        )}
        {address && (
          <div className="flex items-start">
            <span className="mr-2 font-medium text-neutral-110">Address:</span>
            <span>{address}</span>
          </div>
        )}
      </div>
      {(onSendForm || onViewLinks || showViewSentNotes) && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-neutral-75 pt-3">
          {contact.email && onSendForm && (
            <Button
              title="Send a form"
              size="small"
              theme="secondary"
              IconComponent={DocumentTextIcon}
              iconPosition="left"
              onClick={() => onSendForm(contact)}
            />
          )}
          {onViewLinks && (
            <Button
              title="View sent forms"
              size="small"
              theme="secondary"
              IconComponent={LinkIcon}
              iconPosition="left"
              onClick={() => onViewLinks(contact)}
            />
          )}
          {showViewSentNotes && (
            <Button
              title="View sent notes"
              size="small"
              theme="secondary"
              IconComponent={BookOpenIcon}
              iconPosition="left"
              onClick={() => onViewSentNotes?.(contact)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ContactCard;
